import React, { useState, useEffect } from "react"
import { withRouter } from "react-router-dom"
import SignUpApplyStep1Container from "../../containers/SignUpApplyContainer/SignUpApplyStep1Container"
import SignUpApplyStep2Container from "../../containers/SignUpApplyContainer/SignUpApplyStep2Container"
import { ThemeProvider, CssBaseline } from "@material-ui/core"
import { createMuiTheme } from "@material-ui/core/styles"
import services from "../../services"
import { paths } from "../../routes/paths"
import ErrorDialog from "../../components/ErrorDialog"

const SignUpApplyPage = ({ history, match }) => {
  const id = match.params.id
  const isEditMode = !!id
  const [nextStep, setNextStep] = useState(isEditMode)
  const [step1Data, setStep1Data] = useState({})
  const [stepBackClicked, setStepBackClicked] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [application, setApplication] = useState(null)
  const [loading, setLoading] = useState(isEditMode)

  console.log("Edit Mode:", isEditMode)
  console.log("Application ID:", id)

  useEffect(() => {
    if (isEditMode && id) {
      const fetchApplication = async () => {
        try {
          const response =
            await services.userApplicationServices.getUserApplication(id)
          if (response && response.data) {
            setApplication(response.data)

            // Extract step1 data
            const {
              first_name,
              last_name,
              email,
              phone_number,
              business_type,
              business_category,
            } = response.data

            setStep1Data({
              firstName: first_name,
              lastName: last_name,
              email: email,
              phoneNumber: phone_number,
              whichBusinessType: business_type,
              bestDescribes: business_category,
            })

            setLoading(false)
          } else {
            setError("Application data not found")
            setLoading(false)
          }
        } catch (error) {
          console.error("Error fetching application:", error)
          setError(
            error.response?.data?.error ||
              "An error occurred while fetching the application. Please try again.",
          )
          setLoading(false)
        }
      }

      fetchApplication()
    }
  }, [isEditMode, id])

  const handleNext = (formData) => {
    setStep1Data(formData)
    setNextStep(true)
    setStepBackClicked(false)
  }

  const handleBack = () => {
    setNextStep(false)
    setStepBackClicked(true)
  }

  const handleErrorClose = () => {
    setError(null)
  }

  const handleSaveApplyUser = async (values, files = {}) => {
    setIsSubmitting(true)
    setError(null)

    // Create a copy of values without the preference fields and region fields
    const {
      preference1,
      preference2,
      preference3,
      preference4,
      preference5,
      preference6,
      preference7,
      preference8,
      preference9,
      preference10,
      preference11,
      preference12,
      preference13,
      preference14,
      preference15,
      preference16,
      preference17,
      preference18,
      preferenceNA,
      regionUS,
      regionCanada,
      regionEurope,
      regionAsiaPacific,
      regionLatinAmerica,
      regionMiddleEast,
      regionAfrica,
      ...restValues
    } = values

    // Create preferences object with boolean values
    // If N/A is selected, all other preferences should be false
    const preferences = preferenceNA
      ? {
          asianOwned: false,
          blackOwned: false,
          disabledOwned: false,
          hispanicOwned: false,
          lgbtq: false,
          nativeAmericanOwned: false,
          serviceDisabledVeteranOwned: false,
          veteranOwned: false,
          womenOwned: false,
        }
      : {
          asianOwned: Boolean(preference1),
          blackOwned: Boolean(preference2),
          disabledOwned: Boolean(preference3),
          hispanicOwned: Boolean(preference4),
          lgbtq: Boolean(preference5),
          nativeAmericanOwned: Boolean(preference6),
          serviceDisabledVeteranOwned: Boolean(preference7),
          veteranOwned: Boolean(preference8),
          womenOwned: Boolean(preference9),
        }

    // Create business regions string
    const businessRegions = [
      regionUS && "US",
      regionCanada && "Canada",
      regionEurope && "Europe",
      regionAsiaPacific && "Asia-Pacific",
      regionLatinAmerica && "Latin America",
      regionMiddleEast && "Middle East",
      regionAfrica && "Africa",
    ]
      .filter(Boolean)
      .join(", ")

    // Create diversity designations string
    const selectedDesignations = [
      preference1 && "Asian-Owned",
      preference2 && "Black-Owned",
      preference3 && "Disabled-Owned",
      preference4 && "Hispanic/Latin-Owned",
      preference5 && "LGBTQ-Owned",
      preference6 && "Native American-Owned",
      preference7 && "Service-Disabled Veteran-Owned",
      preference8 && "Veteran-Owned",
      preference9 && "Women-Owned",
      preference10 && "Asian-Led",
      preference11 && "Black-Led",
      preference12 && "Disabled-Led",
      preference13 && "Hispanic/Latin-Led",
      preference14 && "LGBTQ-Led",
      preference15 && "Native American-Led",
      preference16 && "Service-Disabled Veteran-Led",
      preference17 && "Veteran-Led",
      preference18 && "Women-Led",
      preferenceNA && "N/A",
    ]
      .filter(Boolean)

    const diversityDesignations =
      values.terms1 === "no" ? "N/A" : selectedDesignations.join(", ")

    // For government, nonprofit, and education categories, use organizationName as companyName
    // if companyName is not provided
    const organizationCategories = ['government', 'nonprofit', 'education']
    const businessCategory = step1Data?.bestDescribes || restValues?.bestDescribes
    
    let finalRestValues = { ...restValues }
    if (
      organizationCategories.includes(businessCategory) &&
      (!finalRestValues.companyName || finalRestValues.companyName.trim() === '') &&
      finalRestValues.organizationName &&
      finalRestValues.organizationName.trim() !== ''
    ) {
      finalRestValues.companyName = finalRestValues.organizationName
    }

    const combinedData = {
      ...step1Data,
      ...finalRestValues,
      businessRegions,
      diversityDesignations,
      preferences,
    }
    console.log("Combined Data:", combinedData)

    try {
      let response

      if (isEditMode && id) {
        // Update existing application (pass files for document uploads)
        response = await services.userApplicationServices.updateApplication(
          id,
          combinedData,
          files,
        )
        // Redirect to user application page with success message
        history.push(paths.userApplication(id) + "?updated=true")
      } else {
        // Create new application
        response = await services.userApplicationServices.signUpApply(
          combinedData,
          files,
        )
        history.push(paths.userApplicationSuccess)
      }
    } catch (error) {
      setError(
        error.response?.data?.error ||
          "An error occurred while submitting your application. Please try again.",
      )
      setIsSubmitting(false)
    }
  }

  const darkTheme = createMuiTheme({
    palette: {
      type: "dark",
    },
  })

  // Show loading state if in edit mode and still loading
  if (isEditMode && loading) {
    return (
      <ThemeProvider theme={darkTheme}>
        <CssBaseline />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <h2>Loading application data...</h2>
        </div>
      </ThemeProvider>
    )
  }

  function hasDiversity(diversityValue) {
    return application.diversity_designations?.includes(diversityValue)
  }

  function hasBusinessRegion(regionValue) {
    return application.business_regions?.includes(regionValue)
  }

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      {nextStep ? (
        <SignUpApplyStep2Container
          history={history}
          handleNext={handleNext}
          handleBack={handleBack}
          handleSaveApplyUser={handleSaveApplyUser}
          isSubmitting={isSubmitting}
          step1Data={step1Data}
          initialValues={
            isEditMode && application
              ? {
                  // Map application data to form field names
                  companyName: application.company_name,
                  companyPhonenumber: application.company_phone_number,
                  streetAddress: application.street_address,
                  addressLine2: application.address_line2,
                  stateRegion: application.state_region,
                  city: application.city,
                  zipCode: application.zip_code,
                  country: application.country,
                  website: application.website,
                  industries: application.industries,
                  services: application.services,
                  terms: application.is_diverse_business ? "yes" : "no",
                  terms1: application.is_diverse_led ? "yes" : "no",
                  customers: application.top_customers,
                  revenueRange: application.revenue_range,
                  certifications: application.certifications,
                  qualityCertifications: application.quality_certifications,
                  additionalInfo: application.additional_info,
                  howDidYouLearn: application.how_did_you_learn,
                  primaryBusiness: application.primary_business,
                  headquartersCountry: application.headquarters_country,
                  isSolopreneur: application.is_solopreneur ? "yes" : "no",
                  organizationName: application.organization_name,
                  organizationPhone: application.organization_phone,
                  title: application.title,
                  areaOfSpecialization: application.area_of_specialization,
                  professionalLevel: application.professional_level,
                  linkedinProfile: application.linkedin_profile,
                  financialServicesType: application.financial_services_type,
                  fundingOpportunities: application.funding_opportunities,
                  industrySpecialization: application.industry_specialization,
                  targetCompanyQualifications: application.target_company_qualifications,
                  revenueRangeAUM: application.revenue_range_aum,
                  providesServicesInMoreThanThreeStates: application.provides_services_in_more_than_three_states ? "yes" : "no",
                  // Map regions to boolean values
                  regionUS: hasBusinessRegion("US"),
                  regionCanada: hasBusinessRegion("Canada"),
                  regionEurope: hasBusinessRegion("Europe"),
                  regionAsiaPacific: hasBusinessRegion("Asia-Pacific"),
                  regionLatinAmerica: hasBusinessRegion("Latin America"),
                  regionMiddleEast: hasBusinessRegion("Middle East"),
                  regionAfrica: hasBusinessRegion("Africa"),
                  // Map preferences to boolean values
                  preference1: hasDiversity("Asian-Owned"),
                  preference2: hasDiversity("Black-Owned"),
                  preference3: hasDiversity("Disabled-Owned"),
                  preference4: hasDiversity("Hispanic/Latin-Owned"),
                  preference5: hasDiversity("LGBTQ-Owned"),
                  preference6: hasDiversity("Native American-Owned"),
                  preference7: hasDiversity("Service-Disabled Veteran-Owned"),
                  preference8: hasDiversity("Veteran-Owned"),
                  preference9: hasDiversity("Women-Owned"),
                  preference10: hasDiversity("Asian-Led"),
                  preference11: hasDiversity("Black-Led"),
                  preference12: hasDiversity("Disabled-Led"),
                  preference13: hasDiversity("Hispanic/Latin-Led"),
                  preference14: hasDiversity("LGBTQ-Led"),
                  preference15: hasDiversity("Native American-Led"),
                  preference16: hasDiversity("Service-Disabled Veteran-Led"),
                  preference17: hasDiversity("Veteran-Led"),
                  preference18: hasDiversity("Women-Led"),
                  preferenceNA: hasDiversity("N/A"),
                }
              : {}
          }
          isEditMode={isEditMode}
        />
      ) : (
        <SignUpApplyStep1Container
          history={history}
          handleNext={handleNext}
          initialValues={step1Data}
          stepBackClicked={stepBackClicked}
        />
      )}
      <ErrorDialog open={!!error} onClose={handleErrorClose} error={error} />
    </ThemeProvider>
  )
}

export default withRouter(SignUpApplyPage)
